import { useCallback, useEffect, useState } from 'react';
import { OVERLAY_MIN_TIME_MS } from '../utils/scrollRestoreUtils';
import { useScrollToEpisode } from './useScrollToEpisode';

interface UseScrollRestoreParams {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  episodesLength: number;
  targetEpisodeId: string | undefined;
  targetCursor: string | null;
  targetCharOffset: string | null;
}

export const useScrollRestore = ({
  scrollContainerRef,
  isLoading,
  episodesLength,
  targetEpisodeId,
  targetCursor,
  targetCharOffset
}: UseScrollRestoreParams) => {
  const [isScrollRestoring, setScrollRestoring] = useState(!!targetEpisodeId);
  const [overlayMinTimePassed, setOverlayMinTimePassed] = useState(false);

  useEffect(() => {
    if (targetEpisodeId) setScrollRestoring(true);
  }, [targetEpisodeId]);

  useEffect(() => {
    if (!targetEpisodeId) return;
    setOverlayMinTimePassed(false);
    const t = setTimeout(() => setOverlayMinTimePassed(true), OVERLAY_MIN_TIME_MS);
    return () => clearTimeout(t);
  }, [targetEpisodeId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (targetEpisodeId && isScrollRestoring) {
      const prevBehavior = container.style.scrollBehavior;
      const prevSnapType = container.style.scrollSnapType;
      container.style.scrollBehavior = 'auto';
      container.style.scrollSnapType = 'none';
      return () => {
        container.style.scrollBehavior = prevBehavior || 'smooth';
        container.style.scrollSnapType = prevSnapType || '';
      };
    }
  }, [targetEpisodeId, isScrollRestoring, scrollContainerRef]);

  const onScrollRestoreComplete = useCallback(() => setScrollRestoring(false), []);

  useScrollToEpisode({
    scrollContainerRef,
    isLoading,
    episodesLength,
    targetEpisodeId,
    targetCursor,
    targetCharOffset,
    onScrollRestoreComplete
  });

  return { isScrollRestoring, overlayMinTimePassed };
};
